import { useState, useMemo, useEffect, useRef } from 'react';
import { X, AlertTriangle, AlertOctagon, Info, ExternalLink, Wind, ChevronRight, Thermometer, CloudLightning, Cloud } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SEVERITY_ORDER = { emergency: 0, warning: 1, advisory: 2 };

const SEVERITY_CONFIG = {
  advisory: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    text: 'text-yellow-900',
    icon: Info,
    iconColor: 'text-yellow-600',
    modalHeader: 'bg-yellow-500',
    label: 'Wildfire Advisory',
  },
  warning: {
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    text: 'text-orange-900',
    icon: AlertTriangle,
    iconColor: 'text-orange-500',
    modalHeader: 'bg-orange-500',
    label: 'Wildfire Warning',
  },
  emergency: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-900',
    icon: AlertOctagon,
    iconColor: 'text-red-600',
    modalHeader: 'bg-red-600',
    label: 'Emergency Notice',
  },
};

function SafetyModal({ alert, config, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl overflow-hidden bg-white"
        style={{ maxHeight: '80vh' }}
      >
        <div className={`${config.modalHeader} px-4 py-3 flex items-center justify-between`}>
          <p className="text-white font-bold text-base">{alert.label || config.label}</p>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <p className="font-bold text-stone-900 text-lg">{alert.title}</p>
            <p className="text-stone-600 text-sm mt-1">{alert.distance}</p>
          </div>

          <a
            href={alert.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#722F37] text-white font-semibold rounded-xl"
          >
            <ExternalLink className="w-4 h-4" />
            Tap here for more information
          </a>

          <div className="border-t border-stone-100 pt-4 space-y-2 text-sm text-stone-700">
            {alert.status && (
              <div className="flex justify-between">
                <span className="text-stone-500">Status</span>
                <span className="font-medium">{alert.status}</span>
              </div>
            )}
            {alert.size && (
              <div className="flex justify-between">
                <span className="text-stone-500">Size</span>
                <span className="font-medium">{alert.size}</span>
              </div>
            )}
            {alert.windTowardUser !== null && alert.windTowardUser !== undefined && (
              <div className="flex justify-between">
                <span className="text-stone-500">Wind</span>
                <span className={`font-medium ${alert.windTowardUser ? 'text-red-600' : 'text-green-700'}`}>
                  {alert.windTowardUser ? 'Blowing toward your route' : 'Blowing away from your route'}
                </span>
              </div>
            )}
            {alert.windSpeed !== null && alert.windSpeed !== undefined && (
              <div className="flex justify-between">
                <span className="text-stone-500">Wind speed</span>
                <span className="font-medium">{alert.windSpeed} km/h</span>
              </div>
            )}
            {alert.type === 'evacuation' && (
              <p className="text-red-700 font-medium pt-1">
                Stay informed and be prepared to leave on short notice. Follow instructions from emergency officials.
              </p>
            )}
            {alert.type === 'air-quality' && (
              <p className="text-amber-700 pt-1">
                Consider carrying any medication (inhalers, etc.) and limiting time outdoors between wineries.
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default function SafetyBanner({ alerts, severity }) {
  const [dismissed, setDismissed] = useState(false);
  const [openAlert, setOpenAlert] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const sorted = useMemo(() =>
    [...(alerts || [])].sort((a, b) =>
      (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
    ), [alerts]);

  const primary = sorted[currentIndex] || sorted[0];

  useEffect(() => {
    if (!dismissed && primary?.severity === 'emergency' && navigator.vibrate) {
      navigator.vibrate([400, 150, 400]);
    }
  }, [dismissed, primary?.severity]);

  if (dismissed || !sorted.length || severity === 'none') return null;
  const config = SEVERITY_CONFIG[primary.severity] || SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.advisory;
  const ALERT_TYPE_ICONS = { 'air-quality': Wind, 'heat': Thermometer, 'thunderstorm': CloudLightning, 'weather': Cloud };
  const Icon = ALERT_TYPE_ICONS[primary.type] || config.icon;

  function cycleNext(e) {
    e.stopPropagation();
    setCurrentIndex((i) => (i + 1) % sorted.length);
  }

  const pulseProps = primary.severity === 'emergency'
    ? {
        animate: { opacity: 1, y: 0, boxShadow: ['0 0 0 0px rgba(239,68,68,0.5)', '0 0 0 8px rgba(239,68,68,0)', '0 0 0 0px rgba(239,68,68,0.5)'] },
        transition: { opacity: { duration: 0.3 }, y: { duration: 0.3 }, boxShadow: { repeat: Infinity, duration: 1.2, ease: 'easeInOut' } }
      }
    : primary.severity === 'warning'
    ? {
        animate: { opacity: 1, y: 0, boxShadow: ['0 0 0 0px rgba(249,115,22,0.4)', '0 0 0 5px rgba(249,115,22,0)', '0 0 0 0px rgba(249,115,22,0.4)'] },
        transition: { opacity: { duration: 0.3 }, y: { duration: 0.3 }, boxShadow: { repeat: Infinity, duration: 2, ease: 'easeInOut' } }
      }
    : {
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 }
      };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        {...pulseProps}
        className={`${config.bg} ${config.border} border rounded-xl px-3 py-2.5 flex items-center gap-2 cursor-pointer`}
        onClick={() => setOpenAlert(primary)}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${config.iconColor} mt-0.5`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${config.text}`}>{primary.banner}</p>
          <p className={`text-xs ${config.iconColor} opacity-70`}>Tap for more details</p>
        </div>
        {sorted.length > 1 && (
          <button
            onClick={cycleNext}
            className={`flex-shrink-0 flex items-center gap-0.5 text-xs font-medium ${config.iconColor} mr-1`}
            aria-label="Next alert"
          >
            {currentIndex + 1}/{sorted.length}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
          className={`flex-shrink-0 ${config.iconColor} hover:opacity-70`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>

      <AnimatePresence>
        {openAlert && (
          <SafetyModal
            alert={openAlert}
            config={config}
            onClose={() => setOpenAlert(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
